import get from 'lodash/get'
import type { DeanonymizationEvent, DeanonymizationProvider } from './types'

type DeanonymizationItem = DeanonymizationEvent['data'][number]

// LiftAi is excluded — it returns anonymous buyer-intent data with a different
// shape (PersonIntentScore / PersonIntentScoreSegment) than the company-level
// deanonymization providers, so the domain/name/country normalization doesn't
// apply. Callers should filter out LiftAi items before invoking this function.
type CompanyLevelProvider = Exclude<DeanonymizationProvider, 'ChiliPiper' | 'LiftAi'>
type NormalizableItem = Exclude<DeanonymizationItem, { provider: 'LiftAi' }>

type NormalizedDeanonymizationEvent = {
  provider: Exclude<DeanonymizationProvider, 'LiftAi'>
  poweredBy: CompanyLevelProvider
  domain: unknown
  name: unknown
  country: unknown
  data: unknown
}

type FieldPaths = {
  domain: string
  name: string
  country: string
}

// https://floatingapps.atlassian.net/wiki/spaces/CP/pages/2412937217/Normalized+deanonymization+events#Provider-mapping
const PROVIDER_PATHS: Record<CompanyLevelProvider, FieldPaths> = {
  '6Sense': {
    domain: 'company.domain',
    name: 'company.name',
    country: 'company.country',
  },
  Bombora: {
    domain: 'domain',
    name: 'company_name',
    country: 'hq_country',
  },
  Leadfeeder: {
    domain: 'company.domain',
    name: 'company.name',
    country: 'location.country_code',
  },
}

export const isLiftAiItem = (
  item: DeanonymizationItem
): item is { provider: 'LiftAi' } & DeanonymizationItem => item.provider === 'LiftAi'

export const normalizeDeanonymizationItem = (
  item: NormalizableItem
): NormalizedDeanonymizationEvent => {
  const poweredBy: CompanyLevelProvider =
    item.provider === 'ChiliPiper' ? item.poweredBy : item.provider

  const paths = PROVIDER_PATHS[poweredBy]

  return {
    provider: item.provider,
    poweredBy,
    domain: get(item.data, paths.domain),
    name: get(item.data, paths.name),
    country: get(item.data, paths.country),
    data: item.data,
  }
}
